import { FieldTypeMap, RelationTypeMap, RuleLogicMap } from './common'

function isEmptyCheckLogic (contentLogicType) {
  return contentLogicType === RuleLogicMap.isEmpty
    || contentLogicType === RuleLogicMap.isNotEmpty
}

function isEmptyValue (value) {
  if (value == null || value === '') {
    return true
  }
  if (Array.isArray(value)) {
    return value.length === 0
  }
  if (typeof value === 'string') {
    return value.trim() === ''
  }
  return false
}

function normalizeScalar (value) {
  if (value == null || value === '') {
    return ''
  }
  if (Array.isArray(value)) {
    return value
      .map(item => {
        if (item == null) return ''
        const str = String(item)
        return str.includes(',') ? str.split(',')[0] : str
      })
      .filter(Boolean)
      .join(',')
  }
  if (typeof value === 'object') {
    return ''
  }
  return String(value)
}

export function buildRuleFormValues (sources = {}) {
  const merged = {}
  const orderedSources = [
    sources.applyInfo,
    sources.positionForm,
    sources.basicForm,
    sources.registerForm,
    sources.extra
  ]
  orderedSources.forEach(source => {
    if (!source || typeof source !== 'object') {
      return
    }
    Object.keys(source).forEach(key => {
      const value = source[key]
      if (value != null && typeof value === 'object' && !Array.isArray(value)) {
        return
      }
      merged[key] = value
    })
  })
  return merged
}

function findRuleByFieldCode (ruleList, fieldCode) {
  return (ruleList || []).find(rule => rule && rule.fieldCode === fieldCode) || null
}

function splitExpectedValues (selectContent) {
  return (selectContent || [])
    .flatMap(item => {
      const raw = item?.value != null ? String(item.value) : ''
      if (!raw || raw === '-') {
        return []
      }
      return raw.replace(/、/g, '|').split('|').map(part => part.trim()).filter(Boolean)
    })
}

function matchInclude (actual, fieldType, selectContent) {
  if (!selectContent || !selectContent.length) {
    return false
  }
  const actualText = normalizeScalar(actual)
  if (!actualText) {
    return false
  }
  if (String(fieldType) === FieldTypeMap.select) {
    return selectContent.some(item => {
      const value = item?.value != null ? String(item.value) : ''
      const label = item?.label != null ? String(item.label) : ''
      return actualText === value || actualText === label
    })
  }
  if (String(fieldType) === FieldTypeMap.cascader && selectContent[0]?.value) {
    try {
      const parsed = JSON.parse(selectContent[0].value)
      return parsed.some(node => {
        return actualText.includes(String(node.code)) || actualText.includes(String(node.name))
      })
    } catch {
      return actualText.includes(String(selectContent[0].value))
    }
  }
  if (String(fieldType) === FieldTypeMap.dateRangePicker) {
    const rangeValue = selectContent[0]?.value
    if (!rangeValue) {
      return false
    }
    const [start, end] = String(rangeValue).split(',')
    return (!start || actualText >= start) && (!end || actualText <= end)
  }
  const expectedValues = splitExpectedValues(selectContent)
  if (expectedValues.length) {
    return expectedValues.some(expected => actualText.includes(expected))
  }
  return selectContent.some(item => {
    const value = item?.value != null ? String(item.value) : ''
    const label = item?.label != null ? String(item.label) : ''
    return (value && actualText.includes(value)) || (label && actualText.includes(label))
  })
}

function matchEqual (actual, selectContent) {
  const actualText = normalizeScalar(actual)
  if (!selectContent || !selectContent.length) {
    return actualText === ''
  }
  const target = selectContent[0]
  const value = target?.value != null ? String(target.value) : ''
  const label = target?.label != null ? String(target.label) : ''
  return actualText === value || actualText === label
}

function compareNumber (actual, selectContent, comparator) {
  const actualNum = Number(normalizeScalar(actual))
  const expectedNum = Number(selectContent?.[0]?.value)
  if (Number.isNaN(actualNum) || Number.isNaN(expectedNum)) {
    return false
  }
  return comparator(actualNum, expectedNum)
}

function evaluateConditionRow (row, formValues) {
  if (!row || !row.fieldCode) {
    return false
  }
  const actual = formValues[row.fieldCode]
  const selectContent = row.selectContent || []

  if (isEmptyCheckLogic(row.contentLogicType)) {
    return row.contentLogicType === RuleLogicMap.isEmpty
      ? isEmptyValue(actual)
      : !isEmptyValue(actual)
  }

  switch (row.contentLogicType) {
    case RuleLogicMap.include:
      return matchInclude(actual, row.fieldType, selectContent)
    case RuleLogicMap.exclude:
      return !matchInclude(actual, row.fieldType, selectContent)
    case RuleLogicMap.equal:
      return matchEqual(actual, selectContent)
    case RuleLogicMap.greaterThan:
      return compareNumber(actual, selectContent, (a, b) => a > b)
    case RuleLogicMap.lessThan:
      return compareNumber(actual, selectContent, (a, b) => a < b)
    default:
      return false
  }
}

function evaluateConditionRows (rows, formValues) {
  const validRows = (rows || []).filter(row => row && row.fieldCode)
  if (!validRows.length) {
    return true
  }
  let result = evaluateConditionRow(validRows[0], formValues)
  for (let i = 1; i < validRows.length; i += 1) {
    const row = validRows[i]
    const rowResult = evaluateConditionRow(row, formValues)
    switch (row.ruleLogicType) {
      case RelationTypeMap.or:
        result = result || rowResult
        break
      case RelationTypeMap.not:
        result = result && !rowResult
        break
      case RelationTypeMap.and:
      default:
        result = result && rowResult
        break
    }
  }
  return result
}

function evaluateConditionGroup (group, formValues) {
  return evaluateConditionRows(group?.childRule, formValues)
}

export function evaluateMainRule (rule, formValues) {
  const groups = rule?.childRule || []
  if (!groups.length) {
    return true
  }
  return groups.some(group => evaluateConditionGroup(group, formValues))
}

export function isApplyFieldVisible (fieldCode, ruleList, formValues) {
  const rule = findRuleByFieldCode(ruleList, fieldCode)
  if (!rule) {
    return true
  }
  return evaluateMainRule(rule, formValues)
}

export function isApplyFieldRequired (field, ruleList, formValues) {
  if (!isApplyFieldVisible(field.fieldCode, ruleList, formValues)) {
    return false
  }
  const rule = findRuleByFieldCode(ruleList, field.fieldCode)
  if (rule) {
    return evaluateMainRule(rule, formValues)
  }
  return String(field.fieldIsNotBlank) === '1'
}

export function filterVisibleFields (fields, ruleList, formValues) {
  return (fields || []).filter(field => {
    return field && isApplyFieldVisible(field.fieldCode, ruleList, formValues)
  }).map(field => ({
    ...field,
    fieldIsNotBlank: isApplyFieldRequired(field, ruleList, formValues) ? '1' : '0'
  }))
}

export const APPLY_FILES_META_KEYS = new Set(['partialObject'])

export function parseApplyFileList (raw) {
  if (raw == null || raw === '') {
    return []
  }
  if (Array.isArray(raw)) {
    return raw
  }
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw)
      if (Array.isArray(parsed)) {
        return parsed
      }
      return parsed ? [parsed] : []
    } catch {
      return []
    }
  }
  return []
}

export function buildAttachmentRuleValues (applyFiles) {
  const fileValues = {}

  Object.keys(applyFiles || {}).forEach(key => {
    if (APPLY_FILES_META_KEYS.has(key)) {
      return
    }
    const raw = applyFiles[key]
    if (raw == null || raw === '') {
      fileValues[key] = ''
      return
    }
    const parsed = parseApplyFileList(raw)
    fileValues[key] = parsed.length > 0 ? parsed : ''
  })

  return fileValues
}

export function buildExamineeRuleFormValues ({ applyInfo, positionInfo }) {
  const root = applyInfo || {}
  return buildRuleFormValues({
    applyInfo: root.applyInfo,
    extra: {
      name: root.name,
      idCard: root.idCard,
      phone: root.phone,
      examineesNumber: root.examineesNumber,
      ...(positionInfo || {}),
      ...buildAttachmentRuleValues(root.applyFiles)
    }
  })
}

export function getRequiredUploadFileFields ({
  allFileFields,
  ruleList,
  rulesLoaded,
  formValues
}) {
  const fields = allFileFields || []
  if (!fields.length) {
    return []
  }
  if (!rulesLoaded) {
    return fields.filter(item => String(item.fieldIsNotBlank) === '1')
  }
  return filterVisibleFields(fields, ruleList, formValues)
    .filter(item => String(item.fieldIsNotBlank) === '1')
}

export function isApplyFilesUploadComplete ({ requiredFields, applyFiles }) {
  const required = requiredFields || []
  if (!required.length) {
    return true
  }
  return required.every(field => {
    const fieldCode = field.fieldCode
    if (!fieldCode) {
      return false
    }
    const files = parseApplyFileList(applyFiles?.[fieldCode])
    return files.length > 0
  })
}
